import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merges Tailwind classes so a later class always wins over an earlier one. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Rule ID6: initials are computed one way, everywhere — up to the first two
 * whitespace-separated tokens of a full name, first character of each,
 * uppercased. A name with more than two tokens (common for an Arabic full
 * name carrying a father's and grandfather's name) still shows only two
 * letters; that is this rule's own deliberate, simple choice, not special
 * -cased per naming culture. `Intl.Segmenter` (locale-aware) rather than a
 * plain slice, so a combining diacritic or an Arabic ligature never splits
 * mid-character. Rule ID2: a missing or empty name falls back to one fixed
 * placeholder, never a blank avatar.
 */
export function initials(name: string | null | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return '?';
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => {
      const first = segmenter.segment(word)[Symbol.iterator]().next();
      return first.done ? '' : first.value.segment.toUpperCase();
    })
    .join('');
}
